// @flow
/* eslint-disable react/no-did-update-set-state */
import React, { Component } from 'react';
import { debounce, findIndex } from 'lodash';
import Brand from '../../UI/Brand';
import { arrayCycle } from '../../../helpers/utils';

type Props = {
  values: Array<any>,
  value: any,
  padding?: number,
  hiddenItems?: number,
  onChange?: (...args: Array<any>) => any
};

type State = {
  index: number,
  itemHeight: number
};

class InfiniteSelect extends Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      index: this.getIndexFromValue(props.value),
      itemHeight: 0
    };

    this.listRef = React.createRef();
    this.selectedRef = React.createRef();

    this.paddedArray = props.values.concat(props.values);

    this.onScroll = this.onScroll.bind(this);
  }

  componentDidMount() {
    const itemHeight = this.listRef.current.firstChild.getBoundingClientRect()
      .height;
    const selectedHeight = this.selectedRef.current.getBoundingClientRect()
      .height;

    const listHeight = itemHeight * this.props.padding * 2 + selectedHeight;

    this.setState({
      scroll: itemHeight * this.props.hiddenItems,
      itemHeight,
      listHeight
    });

    this.listRef.current.addEventListener('onWheel', e => {
      e.preventDefault();
    });
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.value !== this.props.value) {
      this.setState({
        index: this.getIndexFromValue(this.props.value)
      });
    }

    const currentIndex = this.state.index;

    if (currentIndex !== prevState.index) {
      this.onChange(this.props.values[currentIndex]);
    }

    // Don't update scroll after index changes or we go into an infinite loop
    if (
      prevState.scroll === this.state.scroll ||
      currentIndex !== prevState.index
    ) {
      return;
    }

    const { itemHeight } = this.state;

    const defaultScroll = itemHeight * this.props.hiddenItems;
    const scrollTrigger = itemHeight / 2;
    const diff = this.state.scroll - defaultScroll;

    if (Math.abs(diff) < scrollTrigger) {
      return;
    }

    const { values } = this.props;
    const direction = Math.sign(diff);

    this.setState(state => ({
      scroll: state.scroll - itemHeight * direction,
      index: arrayCycle(values, prevState.index, direction)
    }));
  }

  // Takes and array, index and padding and returns a new array where the selected index is in the middle of the array with padded elements either side
  slice(array, index, padding = 2) {
    const paddedIndex = index <= padding ? index + array.length : index;
    return this.paddedArray.slice(
      paddedIndex - padding,
      paddedIndex + padding + 1
    );
  }

  getIndexFromValue(value) {
    return findIndex(this.props.values, obj => obj.value === value);
  }

  requestReset() {
    const { hiddenItems } = this.props;

    this.raf = window.requestAnimationFrame(() => {
      const diff = this.state.itemHeight * hiddenItems - this.state.scroll;
      const direction = Math.sign(diff);

      if (direction === 0) {
        return;
      }

      const delta = Math.abs(diff) > 4 ? 4 : Math.abs(diff);

      this.setState(state => ({
        scroll: state.scroll + direction * delta
      }));

      // Loop and reset
      this.raf = this.requestReset();
    });
  }

  onChange = debounce(
    () => {
      const currentIndex = this.state.index;
      this.props.onChange(this.props.values[currentIndex]);
    },
    250,
    { trailing: true }
  );

  resetScroll = debounce(
    () => {
      window.cancelAnimationFrame(this.raf);
      this.requestReset();
    },
    250,
    { trailing: true }
  );

  onScroll(e) {
    const delta = e.nativeEvent.deltaY;

    // 0 is pixels, 1 is lines
    const mode = e.nativeEvent.deltaMode;
    const newScroll =
      mode === 0 ? delta / 6 : delta * (this.state.itemHeight / 8);

    this.setState(state => ({
      scroll: state.scroll + newScroll
    }));

    this.resetScroll();
  }

  render() {
    const { values, padding, hiddenItems, onChange } = this.props;
    const items = this.slice(
      this.props.values,
      this.state.index,
      padding + hiddenItems
    );

    return (
      <ol
        onWheel={this.onScroll}
        ref={this.listRef}
        className="scroller__menu"
        style={{
          height: `${this.state.listHeight}px`
        }}
      >
        {items.map((item, index) => {
          const isSelected = values[this.state.index].value === item.value;
          const ref = isSelected ? this.selectedRef : null;

          return (
            <Brand disabled={!isSelected} color key={index}>
              <li
                ref={ref}
                className={`scroller__option ${
                  isSelected ? 'scroller__option--is-selected' : ''
                }`}
                style={
                  index === 0
                    ? {
                        marginTop: `${-1 * this.state.scroll}px`
                      }
                    : {}
                }
                onClick={() => onChange(item)}
              >
                {item.label}
              </li>
            </Brand>
          );
        })}
      </ol>
    );
  }
}

InfiniteSelect.defaultProps = {
  padding: 2,
  hiddenItems: 5,
  onChange: () => {}
};

export default InfiniteSelect;
